import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Col, Form, Row } from "antd";
import { observer } from "mobx-react-lite";
import { DEFAULT_LANGUAGE, EUserRole, ServiceTemplateField, TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST, TemplateMetaDataField } from "@devowl-wp/api-real-cookie-banner";
import { useTranslation } from "../../../../../../others/i18n.js";
import { useStores } from "../../../../../../store/root.js";
import { defaultTranslationFlexNamePath, isExtendedFieldHidden } from "../../../../../../util/template/form.js";
import { getDisableStrategies } from "../../../../../../util/template/merge-strategies.js";
import { getFieldNameTranslated } from "../../../../../../util/template/translation.js";
import { getQuillEditorWrapperPropsDefaultLang, hasTranslationFlagsChanged } from "../../../../../../util/translation.js";
import { QuillEditorFormField } from "../../../../../third-party/quill-editor-form.js";
import { QuillSimpleHtmlContent } from "../../../../../third-party/quill-simple-html-content.js";
import { LanguageIcon } from "../../fields/translation/language-icon-addon/language-icon.js";
import { TranslationQuillFormFields } from "../../fields/translation/language-quill-input/language-quill-form-fields.js";
import { FormFieldVisibilityWrapper, FormFieldVisibilityWrapperByCdn } from "../../formFieldVisibilityWrapper.js";
import { TemplateMetaDataExtendsStrategyFormField } from "../../templateMetaDataExtendsStrategyFormField.js";
/**
 * Form field for the legal basis notice.
 * This field allows users to enter a notice about the legal basis of the service template.
 *
 * @returns The form field component for legal basis notice.
 */ const FormFieldServiceTemplateLegalBasisNotice = observer(()=>{
    const { t } = useTranslation();
    const { rcbAuthenticationStore, rcbUserSettingsStore: { settings: { translationSettings } } } = useStores();
    const { isTranslationMode } = translationSettings;
    // Triggers form item re-render on translation mode toggle
    const lastKnownTranslationMode = isTranslationMode;
    const hasCreatorRole = rcbAuthenticationStore.hasRole(EUserRole.Creator);
    const form = Form.useFormInstance();
    return /*#__PURE__*/ _jsx(FormFieldVisibilityWrapperByCdn, {
        children: /*#__PURE__*/ _jsx(FormFieldVisibilityWrapper, {
            fieldNames: ServiceTemplateField.LegalBasisNotice,
            permissionCheck: {
                whenRole: EUserRole.Translator,
                roleWhiteList: TRANSLATABLE_SERVICE_TEMPLATE_ATTRIBUTES_LIST
            },
            forceHiddenFormCallback: isExtendedFieldHidden(ServiceTemplateField.LegalBasisNotice, hasCreatorRole),
            children: /*#__PURE__*/ _jsx(Form.Item, {
                label: getFieldNameTranslated(ServiceTemplateField.LegalBasisNotice, t),
                shouldUpdate: (prev, next)=>prev.extends?.identifier !== next.extends?.identifier || hasTranslationFlagsChanged(prev, next, ServiceTemplateField.LegalBasisNotice) || lastKnownTranslationMode !== isTranslationMode,
                hasFeedback: true,
                children: ({ getFieldValue })=>{
                    const extended = getFieldValue(TemplateMetaDataField.Extends);
                    return /*#__PURE__*/ _jsxs(TemplateMetaDataExtendsStrategyFormField, {
                        extendsFieldName: ServiceTemplateField.LegalBasisNotice,
                        disableStrategies: getDisableStrategies(),
                        onKeepContent: extended && /*#__PURE__*/ _jsx(QuillSimpleHtmlContent, {
                            value: extended[ServiceTemplateField.LegalBasisNotice],
                            readOnly: true
                        }),
                        children: [
                            /*#__PURE__*/ _jsxs(Row, {
                                gutter: [
                                    10,
                                    10
                                ],
                                style: {
                                    flexFlow: "row"
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Col, {
                                        flex: 1,
                                        children: /*#__PURE__*/ _jsx(QuillEditorFormField, {
                                            namePath: ServiceTemplateField.LegalBasisNotice,
                                            quillEditorProps: {
                                                readOnly: !hasCreatorRole,
                                                ...getQuillEditorWrapperPropsDefaultLang(ServiceTemplateField.LegalBasisNotice, form)
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Col, {
                                        flex: "0",
                                        children: /*#__PURE__*/ _jsx(LanguageIcon, {
                                            lang: DEFAULT_LANGUAGE,
                                            flexPathConfig: {
                                                absolute: {
                                                    namePath: ServiceTemplateField.LegalBasisNotice,
                                                    translationNamePath: defaultTranslationFlexNamePath(ServiceTemplateField.LegalBasisNotice)
                                                }
                                            }
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(TranslationQuillFormFields, {
                                flexPathConfig: {
                                    absolute: {
                                        namePath: defaultTranslationFlexNamePath(ServiceTemplateField.LegalBasisNotice)
                                    }
                                }
                            })
                        ]
                    });
                }
            })
        })
    });
});
export { FormFieldServiceTemplateLegalBasisNotice };
